import { useDroppable } from '@dnd-kit/core'
import type { Project } from '@terafile/shared'
import { type ReactNode, useCallback, useEffect, useId, useRef } from 'react'
import { Link } from 'react-router-dom'
import { FETCH_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { hasDraggedFiles } from '../lib/file-drag.js'
import { LOADING } from '../lib/labels.js'
import {
  type ProjectsLocation,
  projectsLocationHref,
  sameProjectsLocation,
} from '../lib/projects-location.js'
import { useUploadActions } from '../lib/upload-manager.js'
import { useFileDragHover } from '../lib/use-file-drag-hover.js'
import { IconChevronRight, IconProject } from './SidebarIcons.js'
import { dropHighlight, fileDropHighlight, sourceRowClass } from './sidebar-row-styles.js'

/**
 * ファイルを閉じたフォルダの上で持ったまま待つと、そのフォルダを開く（Finder と同じ）。
 * 開かないと、中のプロジェクトへ入れるためにドラッグをやめて開き直すことになる。
 */
const SPRING_OPEN_MS = 700

/** ツリーの子を描くのに必要な一覧クエリの状態 */
export type ProjectsState = {
  isLoading: boolean
  error: unknown
}

type SourceTreeNodeProps = {
  dropId: string
  location: ProjectsLocation
  selected: ProjectsLocation | null
  onNavigate?: (() => void) | undefined
  icon: ReactNode
  label: string
  projects: readonly Project[]
  projectsState: ProjectsState
  activeProjectId: string | null
  isExpanded: boolean
  onToggle: () => void
  /** ファイルを持ったまま待ったときに開く。閉じはしない */
  onExpand: () => void
}

/**
 * 開閉できる場所（フォルダ・未分類）。
 *
 * - 行はプロジェクト移動（@dnd-kit）の受け口。子のプロジェクト一覧は含めない。含めると、
 *   子の上にプロジェクトを落としたときにもこのフォルダへの移動になってしまう
 * - ファイルは受け付けない（保存先のプロジェクトが一意に決まらないため）。代わりに
 *   持ったまま待つと開き、中のプロジェクトの上で離せるようにする
 */
export function SourceTreeNode({
  dropId,
  location,
  selected,
  onNavigate,
  icon,
  label,
  projects,
  projectsState,
  activeProjectId,
  isExpanded,
  onToggle,
  onExpand,
}: SourceTreeNodeProps) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId })
  const childListId = useId()
  const isActive = selected !== null && sameProjectsLocation(selected, location)
  const springOpen = useSpringOpen(isExpanded, onExpand)
  const fileHover = useFileDragHover({
    onHoverStart: springOpen.start,
    onHoverEnd: springOpen.cancel,
  })

  return (
    <li {...fileHover.handlers}>
      <div ref={setNodeRef} className={`flex items-center rounded-md ${dropHighlight(isOver)}`}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={childListId}
          aria-label={`${label}を${isExpanded ? '閉じる' : '開く'}`}
          className="grid h-7 w-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <IconChevronRight
            className={`h-3.5 w-3.5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
          />
        </button>
        <Link
          to={projectsLocationHref(location)}
          {...(onNavigate ? { onClick: onNavigate } : {})}
          className={`${sourceRowClass(isActive)} flex-1`}
          aria-current={isActive ? 'page' : undefined}
        >
          <span className="shrink-0 text-muted-foreground">{icon}</span>
          <span className="truncate flex-1">{label}</span>
        </Link>
      </div>
      {isExpanded && (
        <ul id={childListId} className="mt-0.5 space-y-0.5 pl-5">
          <TreeChildren
            projects={projects}
            projectsState={projectsState}
            activeProjectId={activeProjectId}
            onNavigate={onNavigate}
            showFileDropHint={fileHover.isOver}
          />
        </ul>
      )}
    </li>
  )
}

/** ファイルを持ったまま一定時間とどまったら開く */
function useSpringOpen(isExpanded: boolean, onExpand: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isExpandedRef = useRef(isExpanded)
  isExpandedRef.current = isExpanded
  const onExpandRef = useRef(onExpand)
  onExpandRef.current = onExpand

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }, [])
  const start = useCallback(() => {
    if (isExpandedRef.current || timer.current) return
    timer.current = setTimeout(() => {
      timer.current = null
      onExpandRef.current()
    }, SPRING_OPEN_MS)
  }, [])

  // ドラッグ中に行が消えた場合（フォルダ削除など）にタイマーを残さない
  useEffect(() => cancel, [cancel])

  return { start, cancel }
}

function TreeChildren({
  projects,
  projectsState,
  activeProjectId,
  onNavigate,
  showFileDropHint,
}: {
  projects: readonly Project[]
  projectsState: ProjectsState
  activeProjectId: string | null
  onNavigate?: (() => void) | undefined
  showFileDropHint: boolean
}) {
  if (projectsState.isLoading) {
    return <TreeNotice>{LOADING.fetching}</TreeNotice>
  }
  if (projectsState.error) {
    return <TreeNotice>{toUserMessage(projectsState.error, FETCH_ERROR_FALLBACK)}</TreeNotice>
  }
  if (projects.length === 0) {
    return <TreeNotice>プロジェクトはありません</TreeNotice>
  }
  return (
    <>
      {showFileDropHint && <TreeNotice>プロジェクトの上で離すと追加されます</TreeNotice>}
      {projects.map((project) => (
        <ProjectTreeRow
          key={project.id}
          project={project}
          isActive={project.id === activeProjectId}
          onNavigate={onNavigate}
        />
      ))}
    </>
  )
}

/**
 * ツリーのプロジェクト行。OS からのファイルをそのまま受け取ってアップロードする。
 *
 * preventDefault で「処理済み」を示し、window 側（upload-manager）が開いている
 * プロジェクトへ二重に上げないようにする。stopPropagation はしない。すると
 * window 側まで届かず、ドラッグ中の表示が消えなくなる。
 */
function ProjectTreeRow({
  project,
  isActive,
  onNavigate,
}: {
  project: Project
  isActive: boolean
  onNavigate?: (() => void) | undefined
}) {
  const { enqueue } = useUploadActions()
  const fileHover = useFileDragHover()

  return (
    <li
      className="flex items-center"
      onDragEnter={fileHover.handlers.onDragEnter}
      onDragLeave={fileHover.handlers.onDragLeave}
      onDragOver={(e) => {
        if (!hasDraggedFiles(e.dataTransfer)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }}
      onDrop={(e) => {
        fileHover.handlers.onDrop(e)
        if (!hasDraggedFiles(e.dataTransfer)) return
        e.preventDefault()
        if (e.dataTransfer.files.length > 0) {
          enqueue({ projectId: project.id, projectName: project.name }, e.dataTransfer.files)
        }
      }}
    >
      <Link
        to={`/projects/${project.id}`}
        {...(onNavigate ? { onClick: onNavigate } : {})}
        title={project.name}
        className={`${sourceRowClass(isActive)} flex-1 ${fileDropHighlight(fileHover.isOver)}`}
        aria-current={isActive ? 'page' : undefined}
      >
        <span className="shrink-0 text-muted-foreground">
          <IconProject className="h-3.5 w-3.5" />
        </span>
        <span className="truncate flex-1">{project.name}</span>
      </Link>
    </li>
  )
}

function TreeNotice({ children }: { children: ReactNode }) {
  return <li className="px-2 py-1 text-[11px] leading-relaxed text-muted-foreground">{children}</li>
}
